import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
  Collapse,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  NoteAlt as NoteAltIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getArPayments, getUsers } from '../services/api';
import { format } from 'date-fns';
import PageSearchBar from '../components/PageSearchBar';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  if (num === 0) return '$0';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
}

function daysAgo(d) {
  if (!d) return null;
  try {
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return '1 day ago';
    return `${diff} days ago`;
  } catch { return null; }
}

function fmtDateTime(d) {
  if (!d) return null;
  try { return format(new Date(d), 'M/d/yy h:mm a'); } catch { return d; }
}

// Aging color coding
function agingColor(bucket, amount) {
  const n = parseFloat(amount) || 0;
  if (n === 0) return 'inherit';
  switch (bucket) {
    case 'current': return '#2e7d32';  // green
    case '30':      return '#f57c00';  // orange
    case '60':      return '#e65100';  // deep orange
    case '90':      return '#c62828';  // red
    case '120':     return '#7b0000';  // dark red
    default:        return 'inherit';
  }
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

// 2026-07-23 Chris G: added optional `pctOfTotal` so aging cards can show
// their share of Total Outstanding inline next to the dollar value.
const SummaryCard = ({ title, value, color = 'text.primary', subtitle, onClick, active, pctOfTotal }) => (
  <Card
    elevation={active ? 6 : 2}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s',
      borderTop: active ? `3px solid ${color}` : '3px solid transparent',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 6 } : {},
    }}
  >
    <CardContent sx={{ pb: '12px !important' }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight="bold" color={color}>
          {value}
        </Typography>
        {pctOfTotal != null && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            ({pctOfTotal})
          </Typography>
        )}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </CardContent>
  </Card>
);

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const NUMERIC_COLS = ['current_balance', 'over_30', 'over_60', 'over_90', 'over_120', 'balance_60_plus', 'paid_current_month'];
const ALL_SORTABLE_COLS = [...NUMERIC_COLS, 'client_name', 'salesman', 'project_manager', 'last_contact_date', 'notes'];

function descendingComparator(a, b, key) {
  let av = a[key] ?? '';
  let bv = b[key] ?? '';
  if (NUMERIC_COLS.includes(key)) {
    av = parseFloat(av) || 0;
    bv = parseFloat(bv) || 0;
  } else {
    av = String(av).toLowerCase();
    bv = String(bv).toLowerCase();
  }
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Editable fields for the detail panel
const FIELD_GROUPS = [
  {
    label: 'Last Contact',
    fields: [
      { key: 'spoke_with',         label: 'Spoke With',        type: 'text',   md: 3 },
      { key: 'spoke_with_title',   label: 'Title',             type: 'text',   md: 2 },
      { key: 'last_contact_date',  label: 'Date',              type: 'date',   md: 2 },
      { key: 'contact_method',     label: 'Method',            type: 'select', md: 2, options: ['Phone', 'Email', 'In Person', 'Text'] },
      { key: 'contacted_by',       label: 'Contacted By',      type: 'text',   md: 3 },
    ]
  },
  {
    label: 'Outcome & Follow-up',
    fields: [
      { key: 'contact_outcome',    label: 'Contact Outcome',   type: 'text',   md: 4, multiline: true, rows: 2 },
      { key: 'promised_pay_date',  label: 'Promised Pay Date', type: 'text',   md: 2 },
      { key: 'next_action_date',   label: 'Next Action Date',  type: 'text',   md: 2 },
      { key: 'next_action_owner',  label: 'Assign To',         type: 'user_select', md: 4 },
    ]
  },
  {
    label: 'Notes',
    fields: [
      { key: 'collection_notes',   label: 'Add New Note',      type: 'text',   md: 12, multiline: true, rows: 2, placeholder: 'Type a new note — it will be prepended to the history below' },
    ]
  },
];
const EDITABLE_FIELDS = FIELD_GROUPS.flatMap(g => g.fields);

// ─── Detail Panel ─────────────────────────────────────────────────────────────

const DetailPanel = ({ row, canEdit, onSave, actionOwnerUsers = [] }) => {
  const [fields, setFields] = useState(() => {
    const init = {};
    EDITABLE_FIELDS.forEach(f => {
      // collection_notes starts EMPTY — new note gets prepended to history
      init[f.key] = f.key === 'collection_notes' ? '' : (row[f.key] ?? '');
    });
    return init;
  });
  const [saving, setSaving] = useState(false);

  // Re-init when row data changes (e.g. after a save)
  useEffect(() => {
    const init = {};
    EDITABLE_FIELDS.forEach(f => {
      init[f.key] = f.key === 'collection_notes' ? '' : (row[f.key] ?? '');
    });
    setFields(init);
  }, [row]);

  const handleChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await onSave(row.id, fields);
      setSaveSuccess(true);
      // Clear the note input after successful save
      setFields(prev => ({ ...prev, collection_notes: '' }));
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2.5, bgcolor: '#f0f4ff', borderTop: '1px solid', borderColor: 'primary.light' }}>
      {FIELD_GROUPS.map(group => (
        <Box key={group.label} sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
            {group.label}
          </Typography>
          <Grid container spacing={1.5}>
            {group.fields.map(f => (
              <Grid item xs={12} sm={6} md={f.md || 4} key={f.key}>
            {f.type === 'select' ? (
              <FormControl fullWidth size="small" disabled={!canEdit}>
                <InputLabel>{f.label}</InputLabel>
                <Select
                  value={fields[f.key]}
                  label={f.label}
                  onChange={e => handleChange(f.key, e.target.value)}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {f.options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : f.type === 'user_select' ? (
              <FormControl fullWidth size="small" disabled={!canEdit}>
                <InputLabel>{f.label}</InputLabel>
                <Select
                  value={fields[f.key] || ''}
                  label={f.label}
                  onChange={e => handleChange(f.key, e.target.value)}
                >
                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                  {actionOwnerUsers.map(u => (
                    <MenuItem key={u.id} value={u.name}>{u.name} — {u.role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label={f.label}
                type={f.type}
                value={fields[f.key]}
                onChange={e => handleChange(f.key, e.target.value)}
                multiline={f.multiline}
                rows={f.multiline ? (f.rows || 2) : undefined}
                disabled={!canEdit}
                placeholder={f.placeholder || ''}
                slotProps={{ inputLabel: f.type === 'date' ? { shrink: true } : undefined }}
              />
            )}
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Existing notes history (read-only) */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: saveSuccess ? '#e8f5e9' : 'grey.100', borderRadius: 1, maxHeight: 250, overflow: 'auto', border: saveSuccess ? '2px solid #2e7d32' : '1px solid #e0e0e0', transition: 'all 0.3s' }}>
        <Typography variant="caption" fontWeight="bold" color={saveSuccess ? 'success.main' : 'text.secondary'} sx={{ mb: 0.5, display: 'block' }}>
          {saveSuccess ? '✅ Note saved! — Collection Notes History' : '📝 Collection Notes History'}
        </Typography>
        {row.collection_notes ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem' }}>
            {row.collection_notes}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No notes yet. Add one above and click Save.</Typography>
        )}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        {row.last_updated && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {fmtDateTime(row.last_updated)}
          </Typography>
        )}
        {canEdit && (
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ ml: 'auto' }}
          >
            {saving ? 'Saving…' : 'Save Notes'}
          </Button>
        )}
        {!canEdit && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            View only — Management or Finance role required to edit
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ─── Main AR Report page ──────────────────────────────────────────────────────

const ARReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMgmt = user?.role === 'Management' || user?.roles?.includes('Management');
  const isFinance = user?.role === 'Finance' || user?.roles?.includes('Finance');
  const isSales = user?.role === 'Sales' || user?.roles?.includes('Sales');
  const canEdit = isMgmt || isFinance;
  const canViewAR = isMgmt || isFinance || isSales;

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [pmFilter, setPmFilter] = useState('');
  const [salesFilter, setSalesFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('balance_60_plus');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [bucketFilter, setBucketFilter] = useState(null); // null | '30' | '60' | '90' | '120'
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [payments, setPayments] = useState(null);
  const [payView, setPayView] = useState('today');
  const [actionOwnerUsers, setActionOwnerUsers] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (pmFilter) params.pm = pmFilter;
      if (salesFilter) params.salesman = salesFilter;

      const [recordsRes, summaryRes, paymentsRes] = await Promise.all([
        api.get('/ar', { params }),
        api.get('/ar/summary'),
        getArPayments().catch(() => ({ data: null })),
      ]);
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
      if (paymentsRes.data) setPayments(paymentsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access restricted — you do not have permission to view this page.');
      } else {
        setError('Failed to load AR report data.');
      }
    } finally {
      setLoading(false);
    }
  }, [pmFilter, salesFilter]);

  useEffect(() => {
    if (!canViewAR) {
      navigate('/');
      return;
    }
    loadData();
    // Load users for Next Action Owner dropdown
    getUsers().then(r => {
      const users = (r.data || []).filter(u => ['Management', 'Sales', 'PM', 'Finance'].includes(u.role));
      users.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setActionOwnerUsers(users);
    }).catch(() => {});
  }, [canViewAR, loadData, navigate]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/ar/sync');
      await loadData();
    } catch (err) {
      console.error('AR sync error:', err);
      alert(err.response?.data?.error || 'Sync failed — check server logs.');
    } finally {
      setSyncing(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    // Mirror whatever filters the user has on screen so the file matches the
    // view. Requested by Jeanne 2026-06-10.
    setExporting(true);
    try {
      const params = {};
      if (pmFilter)     params.pm = pmFilter;
      if (salesFilter)  params.salesman = salesFilter;
      if (bucketFilter) params.bucket = bucketFilter;
      if (searchTerm)   params.q = searchTerm;
      const res = await api.get('/ar/export.xlsx', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `ACS_AR_Report_${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'A/R report exported' });
    } catch (err) {
      console.error('AR export error:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Export failed — check server logs.',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (col) => {
    if (!ALL_SORTABLE_COLS.includes(col)) return;
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder(NUMERIC_COLS.includes(col) ? 'desc' : 'asc');
    }
  };

  const handleRowClick = (rowId) => {
    setExpandedRowId(prev => (prev === rowId ? null : rowId));
  };

  const handleSaveNotes = async (id, fields) => {
    try {
      const res = await api.put(`/ar/${id}/notes`, fields);
      // Merge saved data back into records so the table reflects changes immediately
      setRecords(prev => prev.map(r => r.id === id ? { ...r, ...res.data } : r));
      setSnackbar({ open: true, message: 'Notes saved' });
    } catch (err) {
      console.error('Save notes error:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save notes' });
    }
  };

  // Unique PM / salesman lists for filters
  const pmList = [...new Set(records.map(r => r.project_manager).filter(Boolean))].sort();
  const salesList = [...new Set(records.map(r => r.salesman).filter(Boolean))].sort();

  // Search filter — client name, AR code, or job number
  const searchFiltered = searchTerm.trim()
    ? records.filter(r => {
        const s = searchTerm.toLowerCase();
        return (r.client_name || '').toLowerCase().includes(s)
          || (r.client_id || '').toLowerCase().includes(s)
          || (r.salesman || '').toLowerCase().includes(s)
          || (r.project_manager || '').toLowerCase().includes(s)
          || (r.collection_notes || '').toLowerCase().includes(s);
      })
    : records;
  // Bucket filter — when a summary card is clicked, show only rows with a balance in that aging bucket
  const filtered = bucketFilter
    ? searchFiltered.filter(r => parseFloat(r[`over_${bucketFilter}`]) > 0)
    : searchFiltered;
  const sorted = [...filtered].sort(getComparator(order, orderBy));

  if (!isMgmt) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            A/R Report
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {isSales
              ? 'Accounts Receivable aging — your projects'
              : 'Accounts Receivable aging'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <PageSearchBar
            placeholder="Search client name, AR code, PM..."
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Project Manager</InputLabel>
            <Select
              value={pmFilter}
              label="Project Manager"
              onChange={(e) => setPmFilter(e.target.value)}
            >
              <MenuItem value="">All PMs</MenuItem>
              {pmList.map(pm => (
                <MenuItem key={pm} value={pm}>{pm}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!isSales && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Salesman</InputLabel>
              <Select
                value={salesFilter}
                label="Salesman"
                onChange={(e) => setSalesFilter(e.target.value)}
              >
                <MenuItem value="">All Salespeople</MenuItem>
                {salesList.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || records.length === 0}
            size="small"
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>

          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleSync}
            disabled={syncing}
            size="small"
          >
            {syncing ? 'Syncing…' : 'Sync from Sage'}
          </Button>
        </Box>
      </Box>

      {/* Summary cards */}
      {summary && (() => {
        // 2026-07-23 Chris G: show each aging bucket's share of Total Outstanding.
        const totalOutstanding = Number(summary.totals?.total_outstanding) || 0;
        const pctOf = (v) => {
          if (!totalOutstanding) return null;
          return `${Math.round((Number(v) || 0) / totalOutstanding * 100)}%`;
        };
        return (
        <>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="Total Outstanding"
                value={fmtMoney(summary.totals?.total_outstanding)}
                color="error.main"
                pctOfTotal={totalOutstanding ? '100%' : null}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="Current"
                value={fmtMoney(summary.totals?.total_current)}
                color="#2e7d32"
                pctOfTotal={pctOf(summary.totals?.total_current)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="30+ Days"
                value={fmtMoney(summary.totals?.total_over_30)}
                color="#f57c00"
                subtitle={bucketFilter === '30' ? '• filtering' : 'click to filter'}
                active={bucketFilter === '30'}
                onClick={() => setBucketFilter(bucketFilter === '30' ? null : '30')}
                pctOfTotal={pctOf(summary.totals?.total_over_30)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="60+ Days"
                value={fmtMoney(summary.totals?.total_over_60)}
                color="#e65100"
                subtitle={bucketFilter === '60' ? '• filtering' : 'click to filter'}
                active={bucketFilter === '60'}
                onClick={() => setBucketFilter(bucketFilter === '60' ? null : '60')}
                pctOfTotal={pctOf(summary.totals?.total_over_60)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="90+ Days"
                value={fmtMoney(summary.totals?.total_over_90)}
                color="#c62828"
                subtitle={bucketFilter === '90' ? '• filtering' : 'click to filter'}
                active={bucketFilter === '90'}
                onClick={() => setBucketFilter(bucketFilter === '90' ? null : '90')}
                pctOfTotal={pctOf(summary.totals?.total_over_90)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="120+ Days"
                value={fmtMoney(summary.totals?.total_over_120)}
                color="#7b0000"
                subtitle={bucketFilter === '120' ? '• filtering' : 'click to filter'}
                active={bucketFilter === '120'}
                onClick={() => setBucketFilter(bucketFilter === '120' ? null : '120')}
                pctOfTotal={pctOf(summary.totals?.total_over_120)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={12 / 7}>
              <SummaryCard
                title="Collected This Month"
                value={fmtMoney(summary.totals?.total_paid_current_month)}
                color="primary.main"
                subtitle="click for drilldown"
                onClick={() => navigate('/ar-collected')}
              />
            </Grid>
            {canEdit && (
              <Grid item xs={6} sm={4} md={12 / 7}>
                <SummaryCard
                  title="MTD Reconciler"
                  value="Open"
                  color="#2e7d32"
                  subtitle="reconcile vs Sage"
                  onClick={() => navigate('/mtd-reconciler')}
                />
              </Grid>
            )}
          </Grid>
          {bucketFilter && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Showing only clients with ${bucketFilter}+ day balances (${filtered.length} of ${records.length})`}
                color="warning"
                size="small"
                onDelete={() => setBucketFilter(null)}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
        </>
        );
      })()}

      {/* Payment Tracker */}
      {payments && canEdit && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #2e7d32' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" fontWeight="bold">💰 Payments Received</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {['today', 'week', 'month'].map(v => (
                <Chip
                  key={v}
                  label={v === 'today' ? `Today ${fmtMoney(payments.today?.total)}` : v === 'week' ? `This Week ${fmtMoney(payments.week?.total)}` : `This Month ${fmtMoney(payments.month?.total)}`}
                  onClick={() => setPayView(v)}
                  color={payView === v ? 'success' : 'default'}
                  variant={payView === v ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>
          </Box>
          {(() => {
            const data = payments[payView];
            if (!data?.clients?.length) return <Typography variant="body2" color="text.secondary">No payments {payView === 'today' ? 'today' : payView === 'week' ? 'this week' : 'this month'}.</Typography>;
            return (
              <Table size="small">
                <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', py: 0.5 } }}>
                  <TableCell>Client</TableCell>
                  <TableCell>AR Code</TableCell>
                  <TableCell align="right">Amount Paid</TableCell>
                  <TableCell align="center">Transactions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.clients.map(c => (
                    <TableRow key={c.ar_code} hover>
                      <TableCell><Typography variant="body2" fontWeight="medium">{c.client_name}</Typography></TableCell>
                      <TableCell><Chip label={c.ar_code} size="small" variant="outlined" /></TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="success.main">{fmtMoney(c.total_paid)}</Typography></TableCell>
                      <TableCell align="center"><Typography variant="caption">{c.txn_count}</Typography></TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid #e0e0e0' } }}>
                    <TableCell colSpan={2}><Typography variant="body2" fontWeight="bold">Total ({data.clients.length} clients)</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="success.main">{fmtMoney(data.total)}</Typography></TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            );
          })()}
        </Paper>
      )}

      {/* By PM and Sales breakdown — side-by-side compact panels */}
      {(summary?.by_pm?.length > 0 || summary?.by_salesman?.length > 0) && (
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          {summary?.by_pm?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 1.25 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5, color: '#0D2C54' }}>Aging by Project Manager</Typography>
                <TableContainer sx={{ maxHeight: 220 }}>
                  <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.75, fontSize: '0.72rem' } }}>
                    <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.68rem', bgcolor: '#f5f5f5' } }}>
                      <TableCell>PM</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">30+</TableCell>
                      <TableCell align="right">60+</TableCell>
                      <TableCell align="right">90+</TableCell>
                      <TableCell align="right">120+</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {summary.by_pm.filter(r => parseFloat(r.total) !== 0).map((r, i) => (
                        <TableRow key={r.project_manager} hover sx={{ bgcolor: i % 2 ? 'action.hover' : undefined }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.project_manager}</TableCell>
                          <TableCell align="right">{fmtMoney(r.current)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_30) > 0 ? '#f57c00' : 'text.secondary' }}>{fmtMoney(r.over_30)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_60) > 0 ? '#e65100' : 'text.secondary' }}>{fmtMoney(r.over_60)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_90) > 0 ? '#c62828' : 'text.secondary' }}>{fmtMoney(r.over_90)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_120) > 0 ? '#b71c1c' : 'text.secondary', fontWeight: parseFloat(r.over_120) > 0 ? 'bold' : 'normal' }}>{fmtMoney(r.over_120)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(r.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
          {summary?.by_salesman?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 1.25 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5, color: '#0D2C54' }}>Aging by Sales Rep</Typography>
                <TableContainer sx={{ maxHeight: 220 }}>
                  <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.75, fontSize: '0.72rem' } }}>
                    <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.68rem', bgcolor: '#f5f5f5' } }}>
                      <TableCell>Sales Rep</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">30+</TableCell>
                      <TableCell align="right">60+</TableCell>
                      <TableCell align="right">90+</TableCell>
                      <TableCell align="right">120+</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {summary.by_salesman.filter(r => parseFloat(r.total) !== 0).map((r, i) => (
                        <TableRow key={r.salesman} hover sx={{ bgcolor: i % 2 ? 'action.hover' : undefined }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.salesman}</TableCell>
                          <TableCell align="right">{fmtMoney(r.current)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_30) > 0 ? '#f57c00' : 'text.secondary' }}>{fmtMoney(r.over_30)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_60) > 0 ? '#e65100' : 'text.secondary' }}>{fmtMoney(r.over_60)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_90) > 0 ? '#c62828' : 'text.secondary' }}>{fmtMoney(r.over_90)}</TableCell>
                          <TableCell align="right" sx={{ color: parseFloat(r.over_120) > 0 ? '#b71c1c' : 'text.secondary', fontWeight: parseFloat(r.over_120) > 0 ? 'bold' : 'normal' }}>{fmtMoney(r.over_120)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(r.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Scroll cue — tells the user the full AR client list is below */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 1, color: 'text.secondary' }}>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          ↓ Full AR Aging — {records.length} clients below
        </Typography>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
      </Box>

      {records.length === 0 ? (
        <Alert severity="info">
          No AR records found. Click "Sync from Sheet" to import data from Google Sheets.
        </Alert>
      ) : (
        <Paper elevation={2}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" color="text.secondary">
              {sorted.length} client{sorted.length !== 1 ? 's' : ''} · Click column headers to sort · Click a row to expand and edit collection notes · Dashed borders = no contact yet
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    { key: 'client_name', label: 'Client', align: 'left', minWidth: 180 },
                    { key: 'current_balance', label: 'Current', align: 'right' },
                    { key: 'over_30', label: '30 Days', align: 'right' },
                    { key: 'over_60', label: '60 Days', align: 'right' },
                    { key: 'over_90', label: '90 Days', align: 'right' },
                    { key: 'over_120', label: '120+', align: 'right' },
                    { key: 'balance_60_plus', label: 'Bal 60+', align: 'right' },
                    { key: 'salesman', label: 'Salesman', align: 'left', minWidth: 100 },
                    { key: 'project_manager', label: 'PM', align: 'left', minWidth: 100 },
                    { key: 'last_contact_date', label: 'Last Contact', align: 'left', minWidth: 90 },
                    { key: 'notes', label: 'Notes', align: 'left', minWidth: 200 },
                  ].map(col => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{ fontWeight: 'bold', bgcolor: 'grey.50', cursor: 'pointer', minWidth: col.minWidth }}
                    >
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row) => {
                  const total = (parseFloat(row.current_balance) || 0)
                    + (parseFloat(row.over_30) || 0)
                    + (parseFloat(row.over_60) || 0)
                    + (parseFloat(row.over_90) || 0)
                    + (parseFloat(row.over_120) || 0);

                  const hasContact = row.last_contact_date || row.contact_method;
                  const hasNotes = row.collection_notes || row.contact_outcome || row.promised_pay_date;
                  const isExpanded = expandedRowId === row.id;

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        onClick={() => handleRowClick(row.id)}
                        sx={{
                          cursor: 'pointer',
                          borderLeft: !hasContact && total > 0 ? '3px dashed' : 'none',
                          borderLeftColor: 'warning.light',
                          bgcolor: isExpanded ? '#dce8ff' : 'inherit',
                          '&.MuiTableRow-hover:hover': {
                            bgcolor: isExpanded ? '#cddcff' : undefined,
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {row.client_name}
                              </Typography>
                              {row.client_id && (
                                <Typography variant="caption" color="text.secondary">
                                  ID: {row.client_id}
                                </Typography>
                              )}
                            </Box>
                            {hasNotes && (
                              <Tooltip title="Has collection notes — click to view/edit">
                                <NoteAltIcon sx={{ fontSize: 14, color: 'primary.main', ml: 0.5, flexShrink: 0 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* Current */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={parseFloat(row.current_balance) > 0 ? 'bold' : 'normal'}
                            sx={{ color: agingColor('current', row.current_balance) }}
                          >
                            {fmtMoney(row.current_balance)}
                          </Typography>
                        </TableCell>

                        {/* 30 days */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={parseFloat(row.over_30) > 0 ? 'bold' : 'normal'}
                            sx={{ color: agingColor('30', row.over_30) }}
                          >
                            {fmtMoney(row.over_30)}
                          </Typography>
                        </TableCell>

                        {/* 60 days */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={parseFloat(row.over_60) > 0 ? 'bold' : 'normal'}
                            sx={{ color: agingColor('60', row.over_60) }}
                          >
                            {fmtMoney(row.over_60)}
                          </Typography>
                        </TableCell>

                        {/* 90 days */}
                        <TableCell align="right">
                          {parseFloat(row.over_90) > 0 ? (
                            <Chip
                              label={fmtMoney(row.over_90)}
                              size="small"
                              sx={{
                                bgcolor: '#c62828',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.72rem',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {/* 120+ days */}
                        <TableCell align="right">
                          {parseFloat(row.over_120) > 0 ? (
                            <Chip
                              label={fmtMoney(row.over_120)}
                              size="small"
                              sx={{
                                bgcolor: '#7b0000',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.72rem',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {/* Balance 60+ */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={parseFloat(row.balance_60_plus) > 0 ? 'bold' : 'normal'}
                            sx={{ color: parseFloat(row.balance_60_plus) > 0 ? '#c62828' : 'text.disabled' }}
                          >
                            {fmtMoney(row.balance_60_plus)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">{row.salesman || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.project_manager || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{fmtDate(row.last_contact_date)}</Typography>
                            {daysAgo(row.last_contact_date) && (
                              <Typography variant="caption" color={(() => { const d = Math.floor((Date.now() - new Date(row.last_contact_date).getTime()) / 86400000); return d > 30 ? 'error.main' : d > 14 ? 'warning.main' : 'success.main'; })()}>
                                {daysAgo(row.last_contact_date)}
                              </Typography>
                            )}
                            {row.contact_method && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.contact_method}
                              </Typography>
                            )}
                            {row.spoke_with && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.spoke_with}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              <Box sx={{ maxWidth: 320 }}>
                                {row.contact_outcome && <Typography variant="caption" display="block">Outcome: {row.contact_outcome}</Typography>}
                                {row.promised_pay_date && <Typography variant="caption" display="block">Promise date: {row.promised_pay_date}</Typography>}
                                {row.next_action_date && <Typography variant="caption" display="block">Next action: {row.next_action_date} ({row.next_action_owner})</Typography>}
                                {row.collection_notes && <Typography variant="caption" display="block">Notes: {row.collection_notes}</Typography>}
                              </Box>
                            }
                            placement="left"
                            arrow
                            disableHoverListener={!row.contact_outcome && !row.collection_notes && !row.promised_pay_date}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                cursor: row.collection_notes ? 'help' : 'default',
                              }}
                            >
                              {row.collection_notes || row.contact_outcome || '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expandable detail panel row */}
                      <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                        <TableCell
                          colSpan={11}
                          sx={{ p: 0, border: isExpanded ? undefined : 'none' }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <DetailPanel
                              row={row}
                              canEdit={canEdit}
                              onSave={handleSaveNotes}
                              actionOwnerUsers={actionOwnerUsers}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Success/error snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

    </Box>
  );
};

export default ARReport;
